import { query } from "../erd-editor-schema/dist/query/index.js";
import { ColumnOption } from "../constants/schema.js";
import { bHas } from "./bit.js";
import { splitsTableName, tableNamePart } from "./tableName.js";
//#region src/utils/tableKeys.ts
var indexListGeneration = 0;
function invalidateTableIndexes() {
	indexListGeneration++;
}
var tableIndexesCache = /* @__PURE__ */ new WeakMap();
function getTableIndexIds({ doc, collections }, tableId) {
	const { indexIds } = doc;
	const entities = collections.indexEntities;
	if (!indexIds.length) return [];
	const cached = tableIndexesCache.get(indexIds);
	if (cached?.generation === indexListGeneration && cached.entities === entities) return cached.byTable.get(tableId) ?? [];
	const byTable = /* @__PURE__ */ new Map();
	for (const index of query(collections).collection("indexEntities").selectByIds(indexIds)) {
		const ids = byTable.get(index.tableId);
		if (ids) ids.push(index.id);
		else byTable.set(index.tableId, [index.id]);
	}
	tableIndexesCache.set(indexIds, {
		generation: indexListGeneration,
		entities,
		byTable
	});
	return byTable.get(tableId) ?? [];
}
function getColumnKeys({ collections, settings }, table) {
	const tableName = splitsTableName(settings.database) ? tableNamePart(table.name, settings.bracketType) : table.name;
	const columns = query(collections).collection("tableColumnEntities").selectByIds(table.columnIds);
	const primaryKeyColumns = columns.filter((column) => bHas(column.options, ColumnOption.primaryKey));
	const keys = primaryKeyColumns.length ? [{
		id: `primaryKey:${table.id}`,
		kind: "primaryKey",
		name: `PK_${tableName}`,
		columnIds: primaryKeyColumns.map((column) => column.id)
	}] : [];
	for (const column of columns) {
		if (!bHas(column.options, ColumnOption.unique)) continue;
		keys.push({
			id: `unique:${column.id}`,
			kind: "unique",
			name: `UQ_${tableName}_${column.name}`,
			columnIds: [column.id]
		});
	}
	return keys;
}
function getUniqueIndexKeys(state, table) {
	const tableColumnIds = new Set(table.columnIds);
	const indexColumns = query(state.collections).collection("indexColumnEntities");
	return query(state.collections).collection("indexEntities").selectByIds(getTableIndexIds(state, table.id)).filter((index) => index.unique).map((index) => ({
		indexId: index.id,
		columnIds: indexColumns.selectByIds(index.indexColumnIds).map((indexColumn) => indexColumn.columnId).filter((columnId) => tableColumnIds.has(columnId))
	}));
}
function getAlternateKeys(state, table) {
	const positions = new Map(table.columnIds.map((id, index) => [id, index]));
	return getUniqueIndexKeys(state, table).filter((key) => key.columnIds.length > 1).sort((a, b) => compareKeys(a, b, positions));
}
function compareKeys(a, b, positions) {
	const length = Math.min(a.columnIds.length, b.columnIds.length);
	for (let index = 0; index < length; index++) {
		const order = positions.get(a.columnIds[index]) - positions.get(b.columnIds[index]);
		if (order) return order;
	}
	if (a.columnIds.length !== b.columnIds.length) return a.columnIds.length - b.columnIds.length;
	return a.indexId < b.indexId ? -1 : 1;
}
function getAlternateKeyMarks(state, table) {
	const marks = {};
	getAlternateKeys(state, table).forEach((key, keyIndex) => {
		key.columnIds.forEach((columnId, columnIndex) => {
			(marks[columnId] ??= []).push(`AK${keyIndex + 1}.${columnIndex + 1}`);
		});
	});
	return Object.fromEntries(Object.entries(marks).map(([columnId, mark]) => [columnId, mark.join(",")]));
}
//#endregion
export { getAlternateKeyMarks, getAlternateKeys, getColumnKeys, getTableIndexIds, getUniqueIndexKeys, invalidateTableIndexes };
